import { coreRoutes } from './routes/core.routes'
import { sharedRoutes } from './routes/shared.routes'
import {
  createRouter,
  createWebHistory,
  type NavigationGuard,
  type RouteRecordRaw,
} from 'vue-router'
import { useAuthStore } from '../stores/auth.store'
import { OrganizationState, useOrganizationStore } from '../stores/organization.store'
import { InitializationState, useInitializationStore } from '../stores/initialization.store'
import { handleOrganizationQuery } from '../core/utils/handle-organization-query'
import { resolveRedirect } from '../core/utils/resolve-redirect'
import { useUserStore } from '../stores/user.store'
import { dialogRoutes } from './routes/dialog.routes'
import { ApplicationModule } from '@/core/models/organization'
import { coachingRoutes } from './routes/coaching.routes'
import { lumiaRoutes } from './routes/lumia.routes'
import { portalRoutes } from './routes/portal.routes'
import { Permission } from '@/core/models/user'

export const initRouter = () =>
  createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    routes: [
      ...brandRoutes(coreRoutes, ApplicationModule.CORE),
      ...brandRoutes(dialogRoutes, ApplicationModule.DIALOG),
      ...brandRoutes(coachingRoutes, ApplicationModule.COACHING),
      ...brandRoutes(lumiaRoutes, ApplicationModule.LUMIA),
      ...sharedRoutes,
      ...portalRoutes,
    ],
  })

const brandRoutes = (routes: RouteRecordRaw[], module: ApplicationModule) => {
  return routes.map((route) => {
    const newRoute = { ...route }

    if (!newRoute.meta) {
      newRoute.meta = {}
    }

    newRoute.meta.module = module

    return newRoute
  })
}

export const authGuard: NavigationGuard = async (to) => {
  if (to.matched.some((record) => record.meta.isPublic)) {
    return true
  }

  const authStore = useAuthStore()

  if (!authStore.isAuthenticated) {
    await authStore.login(window.location.origin + to.fullPath)

    return false
  }

  const urlWithoutOrganization = handleOrganizationQuery(to)
  if (urlWithoutOrganization) {
    return urlWithoutOrganization
  }

  const organizationStore = useOrganizationStore()
  if (
    organizationStore.organizationState !== OrganizationState.Initialized &&
    organizationStore.organizationState !== OrganizationState.PendingAcceptance
  ) {
    await organizationStore.initializeOrganizations()
  }

  if (
    organizationStore.organizationState === OrganizationState.NoOrganization &&
    to.name !== 'choose-organization'
  ) {
    return {
      name: 'choose-organization',
      state: { redirect: to.fullPath },
    }
  }

  if (organizationStore.organizationState === OrganizationState.Unavailable) {
    return {
      name: 'error-page',
      query: { type: 'unavailable' },
    }
  }

  if (organizationStore.organizationState === OrganizationState.Error) {
    return {
      name: 'error-page',
      query: { type: 'noOrganization' },
    }
  }

  if (organizationStore.organizationState === OrganizationState.PendingAcceptance) {
    if (to.name !== 'join-organization') {
      return {
        name: 'join-organization',
        query: { redirect: to.fullPath },
      }
    }
    return true
  }

  if (to.name === 'join-organization') {
    return resolveRedirect(to.query.redirect)
  }

  const initializationStore = useInitializationStore()
  if (
    organizationStore.organizationState == OrganizationState.Initialized &&
    initializationStore.initializationState !== InitializationState.Initialized
  ) {
    await initializationStore.intializeStores()
  }
  if (initializationStore.initializationState === InitializationState.Error) {
    return {
      name: 'error-page',
    }
  }

  const { user } = useUserStore()
  const hasUnacceptedSpecialTerms = user.specialTerms !== null && !user.specialTerms.accepted

  if (hasUnacceptedSpecialTerms && to.name !== 'accept-special-terms') {
    return {
      name: 'accept-special-terms',
      query: { redirect: to.fullPath },
    }
  }

  if (!hasUnacceptedSpecialTerms && to.name === 'accept-special-terms') {
    return resolveRedirect(to.query.redirect)
  }

  const isAdminWithInviteMembers = user.memberPermissions.includes(Permission.ORG_MANAGE_MEMBERS)
  const isOrganizationOnboardingCompleted =
    organizationStore.currentOrganization.onboardingCompleted

  if (
    isAdminWithInviteMembers &&
    !isOrganizationOnboardingCompleted &&
    to.name !== 'admin-onboarding'
  ) {
    return {
      name: 'admin-onboarding',
    }
  }

  if (user.showOnboarding && to.name !== 'onboarding' && to.name !== 'admin-onboarding') {
    return {
      name: 'onboarding',
    }
  }

  const canSeeDashboard =
    user.memberPermissions.includes(Permission.FEEDBACK_RECEIVE) ||
    user.memberPermissions.includes(Permission.FEEDBACK_GIVE)

  const hasCoachManageCyclePermission = user.memberPermissions.includes(
    Permission.COACHING_MANAGE_CYCLE,
  )
  const hasCoachingModule = organizationStore.currentOrganization.modules.includes(
    ApplicationModule.COACHING,
  )
  const hasLumiaModule = organizationStore.currentOrganization.modules.includes(
    ApplicationModule.LUMIA,
  )

  if (to.name === 'dashboard' && hasLumiaModule) {
    return {
      name: 'impulse-library',
    }
  }

  if (
    to.name === 'dashboard' &&
    !canSeeDashboard &&
    hasCoachManageCyclePermission &&
    hasCoachingModule
  ) {
    return {
      name: 'coachees',
    }
  }

  if (
    to.meta.module &&
    to.meta.module != 'education' &&
    organizationStore.currentOrganization.modules.includes(ApplicationModule.EDUCATION)
  ) {
    return {
      name: 'teaching-unit-overview',
    }
  }

  if (
    to.meta.module &&
    to.meta.module === 'education' &&
    !organizationStore.currentOrganization.modules.includes(ApplicationModule.EDUCATION)
  ) {
    return {
      name: 'dashboard',
    }
  }

  if (
    to.meta.module &&
    to.meta.module !== ApplicationModule.CORE &&
    !organizationStore.currentOrganization.modules.includes(to.meta.module)
  ) {
    return {
      name: 'dashboard',
    }
  }

  return true
}
